import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  Box,
  Typography,
  Table,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card
} from '@mui/joy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageLayout from '../components/PageLayout';

const GISData = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await supabase.from('datasets').select('*');
      if (error) {
        console.error('Error fetching datasets:', error);
      } else {
        setDatasets(data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const sortedData = [...datasets].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredDatasets = sortedData.filter(dataset => {
    const matchesCounty = !selectedCounty || (dataset.coverage?.[0] || 'Unknown') === selectedCounty;
    const matchesCategory = !selectedCategory || (dataset.category || 'Uncategorized') === selectedCategory;
    return matchesCounty && matchesCategory;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) {
    return (
      <PageLayout title="GIS Data Manager">
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // Group datasets by coverage
  const groupedByCoverage = filteredDatasets.reduce((acc, dataset) => {
    const coverageKey = dataset.coverage?.[0] && dataset.coverage[0] !== 'Unknown' ? dataset.coverage[0] : 'Statewide';
    if (!acc[coverageKey]) {
      acc[coverageKey] = [];
    }
    acc[coverageKey].push(dataset);
    return acc;
  }, {});

  return (
    <PageLayout title="GIS Data Manager">
      <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <select
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}
              sx={{
                px: 2,
                py: 1,
                fontSize: 'md',
                borderRadius: 'md',
                border: '1px solid',
                borderColor: 'neutral.outlinedBorder',
                bgcolor: 'background.surface',
                color: 'text.primary'
              }}
            >
              <option value="">All Counties</option>
              {[...new Set(datasets.map((d) =>
                d.coverage?.[0] && d.coverage[0] !== 'Unknown' ? d.coverage[0] : 'Statewide'
              ))].sort().map((county) => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              sx={{
                px: 2,
                py: 1,
                fontSize: 'md',
                borderRadius: 'md',
                border: '1px solid',
                borderColor: 'neutral.outlinedBorder',
                bgcolor: 'background.surface',
                color: 'text.primary'
              }}
            >
              <option value="">All Categories</option>
              {[...new Set(datasets.map((d) => d.category || 'Uncategorized'))].sort().map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </Box>
        </Box>
        {Object.entries(groupedByCoverage).map(([coverage, datasets]) => (
          <Card
            key={coverage}
            variant="outlined"
            sx={{ mb: 3, p: 2, boxShadow: 2, borderRadius: 'lg' }}
          >
            <Accordion>
              <AccordionSummary
                sx={{
                  bgcolor: 'success.softBg',
                  color: 'success.solidColor',
                  fontWeight: 'bold',
                  borderRadius: 'sm'
                }}
              >
                <Box sx={{ mr: 1 }}>
                  <ExpandMoreIcon />
                </Box>
                {coverage}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ overflowX: 'auto', borderRadius: 2, p: 0.5, bgcolor: 'background.body' }}>
                  <Table
                    borderAxis="bothBetween"
                    stripe="odd"
                    stickyHeader
                    hoverRow
                    sx={{
                      '& thead th': {
                        textAlign: 'left',
                        fontWeight: 'md',
                        bgcolor: 'neutral.plainHover'
                      },
                      '& tbody td': { verticalAlign: 'top' },
                      borderRadius: 2,
                      minWidth: 700,
                    }}
                  >
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', width: '30%' }}>Name</th>
                        <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Category</th>
                        <th onClick={() => handleSort('last_updated')} style={{ cursor: 'pointer' }}>Last Updated</th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datasets.map((dataset) => (
                        <tr key={dataset.id}>
                          <td>{dataset.name}</td>
                          <td>{dataset.category}</td>
                          <td>{dataset.last_updated || '—'}</td>
                          <td>
                            <Chip color={dataset.status === 'Active' ? 'success' : dataset.status === 'Deprecated' ? 'danger' : 'neutral'}>
                              {dataset.status || 'Unknown'}
                            </Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        ))}
      </Box>
    </PageLayout>
  );
};

export default GISData;